/**
 * StartOutreachListModal — "Start an outreach list" from a Search run
 * (docs/crm-outreach-lists-plan.md §B3). One modal, four steps — Who & what study → Recruiters →
 * First message → Review — with a summary bar that never leaves the screen ("212 people · 106 each
 * for Sarah and Mike · 184 texts start Tue 10:00, last one Tue 5:40 PM").
 *
 * The heavy classification runs ONCE per (run, study) on the server (`preflightOutreach`, cached
 * here per study and debounced while the admin is still clicking between studies). Everything after
 * that — the recently-contacted toggle, the split, who gets texted, the send times — is recomputed
 * client-side by outreachLogic.ts, so ticking a box never costs a round trip. None of it is trusted:
 * `createOutreachList` re-runs every check on the server, and its plain-sentence refusal is what the
 * Review step shows if something changed underneath us.
 *
 * Launch is the irreversible step (people are reopened for the new study, owners change, texts are
 * scheduled), so the Review step restates the whole arithmetic first, and the button stays disabled
 * until every step's check passes.
 *
 * Not portaled: it renders in place inside the CRM's `font-inter` wrapper (the ExportModal
 * precedent), so the typeface and dark classes apply without re-adding them. The saved-template
 * picker it opens sits one tier up (Z.modalRaised) by its own design.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, FileText, Loader2, MessageSquare, Minus, Rocket, Search as SearchIcon, X } from 'lucide-react';
import api from '../../services/api';
import { studyStatusMeta, useTeam } from '../lib';
import VariablePicker from '../components/VariablePicker';
import TemplatePickerModal from '../components/TemplatePickerModal';
import { SegmentedControl } from '../components/filters';
import { insertAtCursor } from '../mergeFields';
import { Alert, Z, btnGhost, btnPrimary, fieldCls } from '../components/ui';
import type { MessageTemplate, StudyOption } from '../types';
import { createOutreachList, isUnavailable, outreachErrorMessage, preflightOutreach } from './api';
import {
  DEFAULT_MESSAGE,
  defaultPace,
  effectiveExclusion,
  lintMessage,
  planSendTimes,
  renderPreview,
  summarizeWizard,
  type WizardOptions,
} from './outreachLogic';
import type { OutreachCandidate, OutreachExclusion, OutreachMessageMode, OutreachPreflightResponse } from './types';
import {
  FIXED_LIST_SETTINGS,
  LIST_NAME_MAX,
  MAX_START_DAYS,
  MESSAGE_VARIABLES,
  PACE_MAX,
  PACE_MIN,
  RECENT_DAY_CHOICES,
  DEFAULT_RECENT_DAYS,
  agentBatchPlan,
  agentPhrase,
  agentReasonSentence,
  buildCreateRequest,
  defaultListName,
  exclusionRows,
  groupStudiesForWizard,
  joinNames,
  localInputValue,
  notTextedRows,
  outUntilLabel,
  ownershipChanges,
  peoplePhrase,
  rawTextBlockCounts,
  reopenedPhrase,
  resolveStart,
  splitDetail,
  splitPhrase,
  stepProblems,
  textsPhrase,
  wizardSummaryParts,
  type StartChoice,
  type WizardStep,
} from './wizardLogic';

export interface StartOutreachListModalProps {
  runId: string;
  searchName: string | null;
  onClose: () => void;
  /** Fired with the new list's id once the server accepted it (the modal then opens the list page). */
  onLaunched: (id: string) => void;
}

const STEPS: Array<{ n: WizardStep; title: string }> = [
  { n: 1, title: 'Who & what study' },
  { n: 2, title: 'Recruiters' },
  { n: 3, title: 'First message' },
  { n: 4, title: 'Review' },
];

const MODE_OPTIONS = [
  { value: 'none', label: 'No message' },
  { value: 'text', label: 'One text' },
  { value: 'agent', label: 'Texting agent' },
];

const START_OPTIONS = [
  { value: 'now', label: 'Now' },
  { value: 'tomorrow', label: 'Tomorrow 9:00' },
  { value: 'pick', label: 'Pick a time' },
];

const labelCls = 'block text-xs font-medium text-gray-600 dark:text-gray-300';
const inputCls = `mt-1 w-full ${fieldCls}`;
const checkCls = 'mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700';
const cardCls = 'rounded-lg border border-gray-200 p-3 dark:border-gray-700';
const mutedCls = 'text-xs text-gray-500 dark:text-gray-400';
const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * A missing route (an api that predates this feature) or a down api reads as "not here yet"; a
 * refusal WE wrote — any response carrying a plain `error` sentence — is shown as itself, so a real
 * "That search run wasn't found" is never swallowed as unavailability.
 */
function isFeatureMissing(e: unknown): boolean {
  const msg = (e as any)?.response?.data?.error;
  return isUnavailable(e) && !(typeof msg === 'string' && msg.trim());
}

function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  } catch {
    return 'America/New_York';
  }
}

export default function StartOutreachListModal({ runId, searchName, onClose, onLaunched }: StartOutreachListModalProps) {
  const navigate = useNavigate();
  const { team, byId: teamById, loading: teamLoading, error: teamError, retry: retryTeam } = useTeam();

  const [step, setStep] = useState<WizardStep>(1);
  const [unavailable, setUnavailable] = useState(false);

  // A clock that moves: "Now" and the recently-contacted window stay honest if the modal sits open.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  // ---- Step 1: study -------------------------------------------------------------------------
  const [studies, setStudies] = useState<StudyOption[] | null>(null);
  const [studiesError, setStudiesError] = useState(false);
  const [studyFilter, setStudyFilter] = useState('');
  const [studyId, setStudyId] = useState('');
  const [choosingStudy, setChoosingStudy] = useState(true);
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [recentOn, setRecentOn] = useState(true);
  const [recentDays, setRecentDays] = useState<number>(DEFAULT_RECENT_DAYS);

  const loadStudies = useCallback(() => {
    setStudiesError(false);
    api.get('/api/crm/studies')
      .then((r) => setStudies((r.data?.studies || []) as StudyOption[]))
      .catch(() => { setStudies([]); setStudiesError(true); });
  }, []);
  useEffect(() => { loadStudies(); }, [loadStudies]);

  const studyGroups = useMemo(() => groupStudiesForWizard(studies || [], studyFilter), [studies, studyFilter]);
  const studyCount = useMemo(() => groupStudiesForWizard(studies || []).reduce((n, g) => n + g.studies.length, 0), [studies]);
  const chosenStudy = useMemo(() => (studies || []).find((s) => s.id === studyId) || null, [studies, studyId]);

  // ---- Preflight: once per (run, study), debounced, cached per study --------------------------
  const [preflight, setPreflight] = useState<OutreachPreflightResponse | null>(null);
  const [pfLoading, setPfLoading] = useState(false);
  const [pfError, setPfError] = useState<string | null>(null);
  const [pfRetry, setPfRetry] = useState(0);
  const pfCache = useRef(new Map<string, OutreachPreflightResponse>());
  const pfSeq = useRef(0);
  useEffect(() => {
    const seq = ++pfSeq.current;
    setPfError(null);
    if (!studyId) { setPreflight(null); setPfLoading(false); return undefined; }
    const cached = pfCache.current.get(studyId);
    if (cached) { setPreflight(cached); setPfLoading(false); return undefined; }
    setPreflight(null);
    setPfLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await preflightOutreach({ search_run_id: runId, study_id: studyId });
        pfCache.current.set(studyId, res);
        if (seq === pfSeq.current) setPreflight(res);
      } catch (e) {
        if (seq !== pfSeq.current) return;
        if (isFeatureMissing(e)) setUnavailable(true);
        else setPfError(outreachErrorMessage(e, "We couldn't check who can be added. Please try again."));
      } finally {
        if (seq === pfSeq.current) setPfLoading(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [runId, studyId, pfRetry]);

  const studyTitle = preflight?.study.title || chosenStudy?.title || chosenStudy?.slug || '';
  const publicTitle = preflight?.study_public_title || studyTitle;
  // From the studies list, not the preflight, so the suggested name doesn't flicker when it lands.
  const defaultName = defaultListName(searchName, chosenStudy?.title || chosenStudy?.slug || studyTitle);
  const name = nameDraft ?? defaultName;
  const tz = preflight?.tenant_timezone || browserTimeZone();

  // ---- Step 2: recruiters --------------------------------------------------------------------
  const [picked, setPicked] = useState<string[]>([]);
  // Team order, not click order: the round-robin (and so who gets whom) must not depend on the
  // order an admin happened to tick the boxes in.
  const recruiterIds = useMemo(() => team.filter((m) => picked.includes(m.id)).map((m) => m.id), [team, picked]);
  const nameOf = useCallback((id: string) => teamById.get(id)?.name || 'A recruiter', [teamById]);
  const togglePicked = (id: string) => setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // ---- Step 3: first message -----------------------------------------------------------------
  const [mode, setModeState] = useState<OutreachMessageMode>('text');
  const [body, setBody] = useState(DEFAULT_MESSAGE);
  const [template, setTemplate] = useState<{ id: string; body: string } | null>(null);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [studyTitleAck, setStudyTitleAck] = useState(false);
  const [includeNotInterested, setIncludeNotInterested] = useState(false);
  const [oldOverride, setOldOverride] = useState(false);
  const [startChoice, setStartChoice] = useState<StartChoice>('now');
  const [pickedStart, setPickedStart] = useState('');
  const [paceDraft, setPaceDraft] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  // A new pace default applies whenever the mode changes: 30/recruiter/hour for texts and the
  // agent's own default are different quantities, so a number typed for one never carries over.
  const setMode = (m: OutreachMessageMode) => { setModeState(m); setPaceDraft(null); };

  // The picked template only stays "the template" while the body is still its text.
  const templateId = template && template.body === body ? template.id : null;

  const [templates, setTemplates] = useState<MessageTemplate[] | null>(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const openTemplates = async () => {
    setTemplatesError(null);
    if (templates) { setPickerOpen(true); return; }
    setTemplatesLoading(true);
    try {
      const r = await api.get('/api/crm/templates', { params: { study_id: studyId || undefined, channel: 'sms' } });
      setTemplates((r.data?.templates || []) as MessageTemplate[]);
      setPickerOpen(true);
    } catch {
      setTemplatesError("Couldn't load the saved templates. Please try again.");
    } finally {
      setTemplatesLoading(false);
    }
  };

  // A different study changes the title the text may name and the templates on offer; a template
  // picked for the old study is no longer "the template" (its text stays in the box).
  useEffect(() => {
    setTemplates(null);
    setTemplate(null);
    setStudyTitleAck(false);
  }, [studyId]);

  // ---- Derived ------------------------------------------------------------------------------
  const wizardOptions: WizardOptions | null = useMemo(() => (preflight ? {
    recruiterIds,
    mode,
    recentlyContactedDays: recentOn ? recentDays : null,
    includeNotInterestedTexts: includeNotInterested,
    textOldLeadsOverride: oldOverride,
    oldLeadStopDays: preflight.limits.old_lead_stop_days,
    oldLeadWarnDays: preflight.limits.old_lead_warn_days,
    now,
  } : null), [preflight, recruiterIds, mode, recentOn, recentDays, includeNotInterested, oldOverride, now]);

  const summary = useMemo(
    () => (preflight && wizardOptions ? summarizeWizard(preflight.candidates, wizardOptions) : null),
    [preflight, wizardOptions],
  );

  // Who each exclusion caught, for the "Show who" disclosures (names only, as the run page shows).
  const excludedPeople = useMemo(() => {
    const out: Partial<Record<OutreachExclusion, OutreachCandidate[]>> = {};
    if (!preflight || !wizardOptions) return out;
    for (const c of preflight.candidates) {
      const ex = effectiveExclusion(c, wizardOptions);
      if (ex && ex !== 'merged') (out[ex] ||= []).push(c);
    }
    return out;
  }, [preflight, wizardOptions]);

  const rawBlocks = useMemo(() => rawTextBlockCounts(summary?.included || []), [summary]);
  const ownership = useMemo(() => ownershipChanges(summary?.included || [], recruiterIds), [summary, recruiterIds]);
  const lint = useMemo(() => lintMessage(body, publicTitle || null), [body, publicTitle]);
  const agent = preflight?.readiness.agent ?? null;
  const agentDefaultPace = agent?.default_pace_per_hour || 20;
  const pace = paceDraft == null ? defaultPace(mode, recruiterIds.length, agentDefaultPace) : Number(paceDraft);
  const start = resolveStart(startChoice, pickedStart, now, tz);
  const sending = mode !== 'none';
  const textPlanned = summary?.textPlanned ?? 0;

  const sendTimes = useMemo(() => {
    if (!sending || !start || textPlanned <= 0 || !Number.isFinite(pace) || pace < PACE_MIN || pace > PACE_MAX) return [];
    return planSendTimes(textPlanned, start, pace, tz);
    // `start` is a fresh Date each render; its instant is the real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sending, start?.getTime(), textPlanned, pace, tz]);

  const agentPlan = agent ? agentBatchPlan(textPlanned, agent) : null;

  const checkInput = {
    name,
    studyId,
    preflightReady: Boolean(preflight),
    preflightLoading: pfLoading,
    summary,
    maxMembers: preflight?.limits.max_members ?? 2000,
    recruiterIds,
    mode,
    lint,
    studyTitleAck,
    start,
    now,
    pace,
    agent,
  };
  const problemsFor = (s: WizardStep) => stepProblems(s, checkInput);
  const currentProblems = problemsFor(step);
  const reachable = (n: WizardStep) => n <= step || STEPS.filter((s) => s.n < n).every((s) => problemsFor(s.n).length === 0);

  const parts = summary
    ? wizardSummaryParts({ summary, recruiterIds, nameOf, mode, sendTimes })
    : null;

  // ---- Launch -------------------------------------------------------------------------------
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const launchingRef = useRef(false);
  const launch = async () => {
    if (launchingRef.current || problemsFor(4).length > 0 || !start) return;
    launchingRef.current = true;
    setLaunching(true);
    setLaunchError(null);
    try {
      const res = await createOutreachList(buildCreateRequest({
        name,
        runId,
        studyId,
        recruiterIds,
        mode,
        body,
        templateId,
        saveAsTemplate,
        recentDays: recentOn ? recentDays : null,
        includeNotInterestedTexts: includeNotInterested,
        textOldLeadsOverride: oldOverride,
        namesStudy: lint.namesStudy,
        studyTitleAck,
        // "Now" means the moment of the click, not the moment the modal last re-rendered.
        start: startChoice === 'now' ? new Date() : start,
        pace,
      }));
      if (!res?.id) {
        setLaunchError('The list was started, but we could not open it. Find it under Outreach lists.');
        return;
      }
      onLaunched(res.id);
      navigate(`/crm/outreach/${res.id}`);
    } catch (e) {
      // No answer at all is ambiguous at THIS step — the server may have created the list before the
      // connection dropped — so say that, rather than "not available" or a blind "try again".
      if (!(e as any)?.response) {
        setLaunchError("We couldn't reach the server, so we don't know whether the list started. Check Outreach lists before trying again.");
      } else if (isFeatureMissing(e)) setUnavailable(true);
      else setLaunchError(outreachErrorMessage(e, "We couldn't launch the list. Please try again."));
    } finally {
      launchingRef.current = false;
      setLaunching(false);
    }
  };

  // ---- Dialog chrome: focus, Escape, scroll lock, Tab wrap ----------------------------------
  const panelRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const blockClose = useRef(false);
  blockClose.current = launching || pickerOpen;
  // Hosts pass an inline arrow; keying the mount effect on it would re-run it on every host
  // re-render and pull focus back to the title while someone is typing. Read it through a ref.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    titleRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !blockClose.current) onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      before?.focus?.();
    };
  }, []);

  const onPanelKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !panelRef.current) return;
    const nodes = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((n) => n.offsetParent !== null || n === document.activeElement);
    if (nodes.length === 0) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === titleRef.current)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  const goTo = (n: WizardStep) => {
    if (!reachable(n)) return;
    setStep(n);
    setLaunchError(null);
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
      stepHeadingRef.current?.focus();
    });
  };

  const chooseStudy = (id: string) => {
    setStudyId(id);
    setChoosingStudy(false);
    setStudyFilter('');
  };

  // ---- Render -------------------------------------------------------------------------------
  const stepTitle = STEPS.find((s) => s.n === step)?.title || '';

  return (
    <div
      className={`fixed inset-0 ${Z.modal} flex items-stretch justify-center bg-black/50 p-0 backdrop-blur-sm sm:items-center sm:p-4`}
      onMouseDown={(e) => { if (e.target === e.currentTarget && !blockClose.current) onClose(); }}
      role="presentation"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="start-outreach-title"
        onKeyDown={onPanelKeyDown}
        className="flex h-full w-full max-w-2xl flex-col overflow-hidden bg-white shadow-xl dark:bg-gray-800 sm:h-auto sm:max-h-[90vh] sm:rounded-xl"
      >
        {/* Header: title + the four steps (two rows on a phone). */}
        <div className="border-b border-gray-200 px-4 pb-3 pt-3 dark:border-gray-700 sm:px-5">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h2 id="start-outreach-title" ref={titleRef} tabIndex={-1} className="text-lg font-semibold text-gray-900 focus:outline-none dark:text-gray-100">
                Start an outreach list
              </h2>
              <p className={`truncate ${mutedCls}`}>From {searchName ? `“${searchName}”` : 'this search'}</p>
            </div>
            <button
              type="button"
              onClick={() => { if (!blockClose.current) onClose(); }}
              aria-label="Close"
              className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700 dark:hover:text-gray-200 sm:h-8 sm:w-8"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {!unavailable && (
            <ol className="mt-2 grid grid-cols-2 gap-1.5 sm:flex sm:flex-wrap" aria-label="Steps">
              {STEPS.map((s) => {
                const current = s.n === step;
                const done = s.n < step;
                return (
                  <li key={s.n}>
                    <button
                      type="button"
                      onClick={() => goTo(s.n)}
                      disabled={!reachable(s.n) || launching}
                      aria-current={current ? 'step' : undefined}
                      className={`inline-flex min-h-[44px] w-full items-center gap-1.5 rounded-full px-2.5 text-xs font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0 sm:w-auto sm:py-1 ${
                        current
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                      }`}
                    >
                      <span className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${current ? 'bg-white/25' : 'bg-white dark:bg-gray-800'}`}>
                        {done ? <Check className="h-3 w-3" aria-hidden="true" /> : s.n}
                      </span>
                      <span className="truncate">{s.title}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {/* The live summary — always on screen, whatever the step. */}
        {!unavailable && (
          <div
            className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200 sm:px-5 sm:text-sm"
            aria-live="polite"
          >
            {!studyId ? (
              <span className="text-gray-500 dark:text-gray-400">Pick a study to see who will be on this list.</span>
            ) : pfLoading ? (
              <span className="inline-flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                Checking everyone from the search against {studyTitle || 'this study'}…
              </span>
            ) : parts ? (
              <span>
                <strong className="font-semibold text-gray-900 dark:text-gray-100">{parts.people}</strong>
                {' · '}{parts.split}
                {' · '}{parts.message}
              </span>
            ) : (
              <span className="text-gray-500 dark:text-gray-400">We couldn't work out the list yet.</span>
            )}
          </div>
        )}

        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">
          {unavailable ? (
            <Alert tone="info" title="Outreach lists aren't available yet.">
              This part of the CRM hasn't been switched on here. Nothing was changed.
            </Alert>
          ) : (
            <>
              <h3 ref={stepHeadingRef} tabIndex={-1} className="sr-only">{`Step ${step} of 4: ${stepTitle}`}</h3>

              {/* ======================= Step 1 — Who & what study ======================= */}
              {step === 1 && (
                <div className="space-y-4">
                  <div>
                    <label htmlFor="outreach-list-name" className={labelCls}>List name</label>
                    <input
                      id="outreach-list-name"
                      value={name}
                      maxLength={LIST_NAME_MAX}
                      onChange={(e) => setNameDraft(e.target.value)}
                      className={inputCls}
                    />
                    <p className={`mt-1 ${mutedCls}`}>Recruiters see this name on their list.</p>
                  </div>

                  <fieldset>
                    <legend className={labelCls}>The study these people are being contacted about</legend>
                    {studies === null ? (
                      <p className={`mt-2 inline-flex items-center gap-1.5 ${mutedCls}`}><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Loading studies…</p>
                    ) : studiesError ? (
                      <Alert tone="error" size="sm" className="mt-2" actions={<button type="button" onClick={loadStudies} className={btnGhost}>Try again</button>}>
                        Couldn't load the studies.
                      </Alert>
                    ) : chosenStudy && !choosingStudy ? (
                      <div className={`mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 ${cardCls}`}>
                        <StudyLabel study={chosenStudy} />
                        <button type="button" onClick={() => setChoosingStudy(true)} className={`${btnGhost} ml-auto`}>Change</button>
                      </div>
                    ) : studyCount === 0 ? (
                      <p className={`mt-2 ${mutedCls}`}>There are no studies to pick from yet.</p>
                    ) : (
                      <div className="mt-1">
                        {studyCount > 6 && (
                          <div className="relative mb-1.5">
                            <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                            <input
                              value={studyFilter}
                              onChange={(e) => setStudyFilter(e.target.value)}
                              placeholder="Find a study"
                              aria-label="Find a study"
                              className={`w-full pl-8 ${fieldCls}`}
                            />
                          </div>
                        )}
                        <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                          {studyGroups.length === 0 && <p className={`px-3 py-3 ${mutedCls}`}>No study matches that.</p>}
                          {studyGroups.map((g) => (
                            <div key={g.label}>
                              <p className="sticky top-0 bg-gray-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:bg-gray-900 dark:text-gray-500">{g.label}</p>
                              {g.studies.map((s) => (
                                <label
                                  key={s.id!}
                                  className="flex min-h-[44px] cursor-pointer items-center gap-2.5 px-3 py-1.5 transition-colors duration-100 hover:bg-gray-50 dark:hover:bg-gray-700/40 sm:min-h-0"
                                >
                                  <input
                                    type="radio"
                                    name="outreach-study"
                                    value={s.id!}
                                    checked={studyId === s.id}
                                    onChange={() => chooseStudy(s.id!)}
                                    className="h-4 w-4 flex-shrink-0 border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                                  />
                                  <StudyLabel study={s} />
                                </label>
                              ))}
                            </div>
                          ))}
                        </div>
                        {chosenStudy && (
                          <button type="button" onClick={() => setChoosingStudy(false)} className={`mt-1.5 ${btnGhost}`}>Keep {chosenStudy.title || chosenStudy.slug}</button>
                        )}
                      </div>
                    )}
                  </fieldset>

                  {studyId && preflight && (
                    <Readiness preflight={preflight} />
                  )}

                  {studyId && pfError && (
                    <Alert tone="error" size="sm" actions={<button type="button" onClick={() => setPfRetry((n) => n + 1)} className={btnGhost}>Try again</button>}>
                      {pfError}
                    </Alert>
                  )}

                  {studyId && pfLoading && (
                    <p className={`inline-flex items-center gap-1.5 ${mutedCls}`}>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                      Checking everyone from the search — this can take a few seconds for a long list.
                    </p>
                  )}

                  {preflight && summary && (
                    <div className="space-y-3">
                      <div className={cardCls}>
                        <p className="text-sm text-gray-900 dark:text-gray-100">
                          <strong className="font-semibold">{peoplePhrase(summary.included.length)}</strong> will be on the list
                          <span className="text-gray-500 dark:text-gray-400"> — of {summary.total.toLocaleString()} found by the search</span>
                        </p>
                        {summary.excludedTotal > 0 && (
                          <>
                            <p className={`mt-2 font-medium ${mutedCls}`}>{peoplePhrase(summary.excludedTotal)} left off:</p>
                            <ul className="mt-1 space-y-1.5">
                              {exclusionRows(summary.excluded).map((row) => (
                                <li key={row.code} className="text-sm">
                                  <details className="group">
                                    <summary className="flex cursor-pointer list-none items-baseline gap-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 [&::-webkit-details-marker]:hidden">
                                      <span className="min-w-0 flex-1 text-gray-800 dark:text-gray-100">
                                        {row.label}
                                        {row.hint && <span className="block text-xs text-gray-500 dark:text-gray-400">{row.hint}</span>}
                                      </span>
                                      <span className="tabular-nums font-medium text-gray-700 dark:text-gray-200">{row.count.toLocaleString()}</span>
                                      <span className="text-xs text-primary-700 group-open:hidden dark:text-primary-400">Show who</span>
                                      <span className="hidden text-xs text-primary-700 group-open:inline dark:text-primary-400">Hide</span>
                                    </summary>
                                    <ul className="mt-1 max-h-40 overflow-y-auto rounded border border-gray-100 py-1 text-xs dark:border-gray-700">
                                      {(excludedPeople[row.code] || []).slice(0, 50).map((c) => (
                                        <li key={c.contact_id} className="flex gap-2 px-2 py-0.5 text-gray-700 dark:text-gray-200">
                                          <span className="min-w-0 flex-1 truncate">{c.name || 'No name on file'}</span>
                                          {c.excluded_detail && <span className="text-gray-500 dark:text-gray-400">{c.excluded_detail}</span>}
                                        </li>
                                      ))}
                                      {(excludedPeople[row.code] || []).length > 50 && (
                                        <li className="px-2 py-0.5 text-gray-400">and {((excludedPeople[row.code] || []).length - 50).toLocaleString()} more</li>
                                      )}
                                    </ul>
                                  </details>
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2.5 text-sm text-gray-800 dark:border-gray-700 dark:text-gray-100">
                          <input
                            id="outreach-recent"
                            type="checkbox"
                            checked={recentOn}
                            onChange={(e) => setRecentOn(e.target.checked)}
                            className={checkCls}
                          />
                          <label htmlFor="outreach-recent">Leave off anyone we contacted in the last</label>
                          <select
                            value={recentDays}
                            onChange={(e) => { setRecentDays(Number(e.target.value)); setRecentOn(true); }}
                            aria-label="How many days counts as recently contacted"
                            className={`w-auto ${fieldCls}`}
                          >
                            {RECENT_DAY_CHOICES.map((d) => <option key={d} value={d}>{d} days</option>)}
                          </select>
                        </div>
                      </div>

                      <ul className="space-y-1.5 text-sm text-gray-700 dark:text-gray-200">
                        {summary.reopened > 0 && (
                          <li>
                            <strong className="font-semibold">{peoplePhrase(summary.reopened)}</strong> will be reopened for {studyTitle}
                            {' '}<span className="text-gray-500 dark:text-gray-400">(was {reopenedPhrase(summary.reopenedByPrior)})</span>.
                          </li>
                        )}
                        {summary.resumed > 0 && (
                          <li><strong className="font-semibold">{peoplePhrase(summary.resumed)}</strong> will pick up where they left off on {studyTitle}.</li>
                        )}
                        {summary.stoppedAutomations > 0 && (
                          <li>
                            <strong className="font-semibold">{summary.stoppedAutomations.toLocaleString()}</strong> old automated {summary.stoppedAutomations === 1 ? 'message' : 'messages'} will be stopped.
                          </li>
                        )}
                        <li className="text-gray-500 dark:text-gray-400">
                          Stage-conversion reports will count these people under {studyTitle} and their new recruiter from now on.
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* ============================ Step 2 — Recruiters ============================ */}
              {step === 2 && (
                <div className="space-y-3">
                  <fieldset>
                    <legend className={labelCls}>Split the list between</legend>
                    <p className={`mt-0.5 ${mutedCls}`}>
                      Each recruiter becomes the owner of the people they're given, so replies, calls and alerts reach them.
                    </p>
                    {teamLoading && team.length === 0 ? (
                      <p className={`mt-2 inline-flex items-center gap-1.5 ${mutedCls}`}><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Loading the team…</p>
                    ) : teamError && team.length === 0 ? (
                      <Alert tone="error" size="sm" className="mt-2" actions={<button type="button" onClick={retryTeam} className={btnGhost}>Try again</button>}>
                        Couldn't load the team.
                      </Alert>
                    ) : (
                      <ul className="mt-2 max-h-72 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
                        {team.map((m) => {
                          const on = picked.includes(m.id);
                          const out = outUntilLabel(m, now);
                          return (
                            <li key={m.id}>
                              <label className="flex min-h-[44px] cursor-pointer items-center gap-2.5 px-3 py-1.5 transition-colors duration-100 hover:bg-gray-50 dark:hover:bg-gray-700/40 sm:min-h-0">
                                <input type="checkbox" checked={on} onChange={() => togglePicked(m.id)} className="h-4 w-4 flex-shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700" />
                                <span className="min-w-0 flex-1 truncate text-sm text-gray-900 dark:text-gray-100">{m.name}</span>
                                {out && (
                                  <span className="whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">{out}</span>
                                )}
                                {on && summary && (
                                  <span className="whitespace-nowrap text-xs tabular-nums text-gray-500 dark:text-gray-400">{peoplePhrase(summary.split[m.id] ?? 0)}</span>
                                )}
                              </label>
                            </li>
                          );
                        })}
                        {team.length === 0 && <li className={`px-3 py-3 ${mutedCls}`}>Nobody on the team yet.</li>}
                      </ul>
                    )}
                  </fieldset>

                  {recruiterIds.length > 0 && summary && (
                    <ul className="space-y-1.5 text-sm text-gray-700 dark:text-gray-200">
                      <li className="font-medium text-gray-900 dark:text-gray-100">{splitDetail(summary.split, recruiterIds, nameOf)}</li>
                      {ownership.fromOthers > 0 && (
                        <li>
                          <strong className="font-semibold">{ownership.fromOthers.toLocaleString()}</strong> currently {ownership.fromOthers === 1 ? 'belongs' : 'belong'} to someone else — they'll move to {recruiterIds.length === 1 ? nameOf(recruiterIds[0]) : 'these recruiters'}.
                        </li>
                      )}
                      {ownership.unowned > 0 && (
                        <li><strong className="font-semibold">{ownership.unowned.toLocaleString()}</strong> {ownership.unowned === 1 ? 'has' : 'have'} no owner yet — they'll get one.</li>
                      )}
                      {recruiterIds.map((id) => {
                        const out = outUntilLabel(teamById.get(id) || {}, now);
                        return out ? (
                          <li key={id} className="text-amber-700 dark:text-amber-300">
                            {nameOf(id)} is {out.charAt(0).toLowerCase()}{out.slice(1)} — their people wait until they're back, or you can share them out from the list page.
                          </li>
                        ) : null;
                      })}
                      <li className="text-gray-500 dark:text-gray-400">{joinNames(recruiterIds.map(nameOf))} will be notified.</li>
                    </ul>
                  )}
                </div>
              )}

              {/* ========================== Step 3 — First message ========================== */}
              {step === 3 && (
                <div className="space-y-4">
                  <SegmentedControl options={MODE_OPTIONS} value={mode} onChange={(v) => setMode(v as OutreachMessageMode)} label="First message" />

                  {mode === 'none' && (
                    <p className="text-sm text-gray-700 dark:text-gray-200">
                      Nobody gets a message. Recruiters call through the list, freshest contacts first.
                    </p>
                  )}

                  {mode === 'text' && (
                    <>
                      <div>
                        <div className="flex flex-wrap items-end justify-between gap-2">
                          <label htmlFor="outreach-message" className={labelCls}>The text</label>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <VariablePicker
                              items={MESSAGE_VARIABLES}
                              onInsert={(token) => insertAtCursor(bodyRef.current, body, setBody, token)}
                            />
                            <button
                              type="button"
                              onClick={openTemplates}
                              disabled={templatesLoading}
                              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
                            >
                              {templatesLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <FileText className="h-3.5 w-3.5" aria-hidden="true" />}
                              Use a saved template
                            </button>
                          </div>
                        </div>
                        <textarea
                          id="outreach-message"
                          ref={bodyRef}
                          value={body}
                          onChange={(e) => setBody(e.target.value)}
                          rows={6}
                          aria-describedby="outreach-message-count outreach-message-rules"
                          className={`${inputCls} resize-y leading-snug`}
                        />
                        <p id="outreach-message-count" className={`mt-1 ${mutedCls}`}>
                          {lint.chars.toLocaleString()} characters · {lint.segments === 1 ? 'fits in 1 text' : `${lint.segments} texts long`}
                          {templateId && ' · from a saved template'}
                        </p>
                        {templatesError && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{templatesError}</p>}
                        <div id="outreach-message-rules" aria-live="polite">
                          {lint.errors.length > 0 && (
                            <ul className="mt-1.5 space-y-0.5 text-xs text-red-600 dark:text-red-400">
                              {lint.errors.map((err) => <li key={err}>{err}</li>)}
                            </ul>
                          )}
                          {lint.warnings.map((w) => <p key={w} className="mt-1 text-xs text-amber-700 dark:text-amber-300">{w}</p>)}
                        </div>
                      </div>

                      {lint.namesStudy && (
                        <label className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                          <input type="checkbox" checked={studyTitleAck} onChange={(e) => setStudyTitleAck(e.target.checked)} className={checkCls} />
                          <span>This text names the study, which can reveal a health condition to whoever holds the phone.</span>
                        </label>
                      )}

                      <Preview
                        heading={preflight?.preview_contact ? `How ${previewName(preflight)} will see it` : 'Preview'}
                        note="The person on the list we last heard from longest ago."
                        text={preflight?.preview_contact ? renderPreview(body, {
                          first_name: preflight.preview_contact.first_name,
                          last_name: preflight.preview_contact.last_name,
                          study_title: publicTitle,
                        }) : null}
                      />

                      <NotTexted
                        notTexted={summary?.notTexted || {}}
                        rawNotInterested={rawBlocks.prior_not_interested ?? 0}
                        rawTooOld={rawBlocks.too_old ?? 0}
                        includeNotInterested={includeNotInterested}
                        onIncludeNotInterested={setIncludeNotInterested}
                        oldOverride={oldOverride}
                        onOldOverride={setOldOverride}
                        oldLeadWarn={summary?.oldLeadWarn ?? 0}
                        warnDays={preflight?.limits.old_lead_warn_days ?? 90}
                      />

                      <StartAndPace
                        startChoice={startChoice}
                        onStartChoice={setStartChoice}
                        pickedStart={pickedStart}
                        onPickedStart={setPickedStart}
                        now={now}
                        paceText={paceDraft ?? String(pace)}
                        onPace={setPaceDraft}
                        paceHint="The usual pace is 30 an hour for each recruiter, so replies don't all land at once."
                        what="texts"
                        outcome={textsPhrase(textPlanned, sendTimes)}
                      />

                      {!templateId && (
                        <label className="flex items-start gap-2 text-sm text-gray-800 dark:text-gray-100">
                          <input type="checkbox" checked={saveAsTemplate} onChange={(e) => setSaveAsTemplate(e.target.checked)} className={checkCls} />
                          <span>Save this message as a template for {studyTitle || 'this study'}</span>
                        </label>
                      )}
                    </>
                  )}

                  {mode === 'agent' && (
                    <>
                      {!agent || !agent.available ? (
                        <Alert tone="warning" size="sm" title="The texting agent can't take this list.">
                          {agentReasonSentence(agent?.reason_code)} Choose One text or No message instead.
                        </Alert>
                      ) : (
                        <>
                          <p className="text-sm text-gray-700 dark:text-gray-200">
                            The agent sends its published opening text for a new study — it doesn't name the study — then answers replies and hands people to their recruiter when they need a person.
                          </p>
                          {agent.opener_preview && (
                            <Preview
                              heading={preflight?.preview_contact ? `How ${previewName(preflight)} will see the opening text` : 'The opening text'}
                              note="Later messages are written by the agent from each reply."
                              text={renderPreview(agent.opener_preview, {
                                first_name: preflight?.preview_contact?.first_name,
                                last_name: preflight?.preview_contact?.last_name,
                                study_title: publicTitle,
                              })}
                            />
                          )}
                          {agentPlan && textPlanned > 0 && (
                            <p className={`text-sm ${agentPlan.fits ? 'text-gray-700 dark:text-gray-200' : 'font-medium text-red-600 dark:text-red-400'}`}>
                              {peoplePhrase(textPlanned)} → {agentPlan.batches.toLocaleString()} agent {agentPlan.batches === 1 ? 'batch' : 'batches'} of up to {agentPlan.perBatch.toLocaleString()}.
                              {' '}{agentPlan.freeSlots.toLocaleString()} of {agent.max_active.toLocaleString()} batch slots {agentPlan.freeSlots === 1 ? 'is' : 'are'} free.
                              <span className="block text-xs font-normal text-gray-500 dark:text-gray-400">
                                Each batch holds its slot for up to 30 days while the agent handles replies.
                              </span>
                            </p>
                          )}
                          <NotTexted
                            notTexted={summary?.notTexted || {}}
                            rawNotInterested={rawBlocks.prior_not_interested ?? 0}
                            rawTooOld={rawBlocks.too_old ?? 0}
                            includeNotInterested={includeNotInterested}
                            onIncludeNotInterested={setIncludeNotInterested}
                            oldOverride={oldOverride}
                            onOldOverride={setOldOverride}
                            oldLeadWarn={summary?.oldLeadWarn ?? 0}
                            warnDays={preflight?.limits.old_lead_warn_days ?? 90}
                          />
                          <StartAndPace
                            startChoice={startChoice}
                            onStartChoice={setStartChoice}
                            pickedStart={pickedStart}
                            onPickedStart={setPickedStart}
                            now={now}
                            paceText={paceDraft ?? String(pace)}
                            onPace={setPaceDraft}
                            paceHint={`The agent's usual pace is ${agentDefaultPace.toLocaleString()} an hour.`}
                            what="opening texts"
                            outcome={agentPhrase(textPlanned, sendTimes)}
                          />
                          <p className="rounded-lg bg-gray-50 p-2.5 text-xs text-gray-600 dark:bg-gray-900/40 dark:text-gray-300">
                            The agent answers replies. Recruiters are served these people after {FIXED_LIST_SETTINGS.agent_call_delay_days} days if the agent hasn't reached them. Calling someone stops the agent from picking up their reply for 24 hours.
                          </p>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ============================== Step 4 — Review ============================== */}
              {step === 4 && preflight && summary && (
                <div className="space-y-3">
                  <dl className="divide-y divide-gray-100 rounded-lg border border-gray-200 text-sm dark:divide-gray-700 dark:border-gray-700">
                    <ReviewRow term="List">
                      “{name.trim()}” for {studyTitle}
                      {preflight.study.internal_title && preflight.study.internal_title !== studyTitle && (
                        <span className="text-gray-500 dark:text-gray-400"> ({preflight.study.internal_title})</span>
                      )}
                    </ReviewRow>
                    <ReviewRow term="People">
                      {summary.total.toLocaleString()} found by the search · {summary.excludedTotal.toLocaleString()} left off ·{' '}
                      <strong className="font-semibold">{summary.included.length.toLocaleString()} on the list</strong>
                    </ReviewRow>
                    <ReviewRow term="Recruiters">
                      {splitPhrase(summary.split, recruiterIds, nameOf).replace(/^./, (c) => c.toUpperCase())}.
                      {(ownership.fromOthers + ownership.unowned) > 0 && ` ${(ownership.fromOthers + ownership.unowned).toLocaleString()} ${(ownership.fromOthers + ownership.unowned) === 1 ? 'gets' : 'get'} a new owner.`}
                      {' '}{joinNames(recruiterIds.map(nameOf))} will be notified.
                    </ReviewRow>
                    {(summary.reopened > 0 || summary.resumed > 0 || summary.stoppedAutomations > 0) && (
                      <ReviewRow term="Changes">
                        {[
                          summary.reopened > 0 ? `${summary.reopened.toLocaleString()} reopened for ${studyTitle} (was ${reopenedPhrase(summary.reopenedByPrior)})` : null,
                          summary.resumed > 0 ? `${summary.resumed.toLocaleString()} pick up where they left off` : null,
                          summary.stoppedAutomations > 0 ? `${summary.stoppedAutomations.toLocaleString()} old automated ${summary.stoppedAutomations === 1 ? 'message' : 'messages'} stopped` : null,
                        ].filter(Boolean).join(' · ')}.
                      </ReviewRow>
                    )}
                    <ReviewRow term="First message">
                      {mode === 'none' && 'None — recruiters call through the list.'}
                      {mode === 'text' && (
                        <>
                          {capitalize(textsPhrase(textPlanned, sendTimes))}, about {pace.toLocaleString()} an hour, only between 8am and 9pm.
                          {' '}Recruiters are served each texted person {FIXED_LIST_SETTINGS.call_after_text_hours} hours after their text.
                          {notTextedTotal(summary.notTexted) > 0 && ` ${notTextedTotal(summary.notTexted).toLocaleString()} won't be texted (${notTextedRows(summary.notTexted).map((r) => `${r.label.toLowerCase()} ${r.count.toLocaleString()}`).join(' · ')}) — they stay on the list for calls.`}
                          {templateId ? ' Uses a saved template.' : saveAsTemplate ? ` The text will be saved as a template for ${studyTitle}.` : ''}
                        </>
                      )}
                      {mode === 'agent' && agentPlan && (
                        <>
                          {capitalize(agentPhrase(textPlanned, sendTimes))} in {agentPlan.batches.toLocaleString()} {agentPlan.batches === 1 ? 'batch' : 'batches'}, about {pace.toLocaleString()} an hour.
                          {' '}Recruiters are served these people after {FIXED_LIST_SETTINGS.agent_call_delay_days} days if the agent hasn't reached them.
                          {notTextedTotal(summary.notTexted) > 0 && ` ${notTextedTotal(summary.notTexted).toLocaleString()} won't be texted — they stay on the list for calls.`}
                        </>
                      )}
                    </ReviewRow>
                    <ReviewRow term="Tries">
                      Each person gets up to {FIXED_LIST_SETTINGS.max_attempts} tries, about a day apart. Anyone who never answers or replies is marked Unreachable after the last one.
                    </ReviewRow>
                  </dl>
                  <p className={mutedCls}>
                    Stage-conversion reports will count these people under {studyTitle} and their new recruiter from now on.
                  </p>
                  {currentProblems.length > 0 && (
                    <Alert tone="warning" size="sm" title="Before you launch">
                      <ul className="list-disc pl-4">{currentProblems.map((p) => <li key={p}>{p}</li>)}</ul>
                    </Alert>
                  )}
                  {launchError && <Alert tone="error" size="sm">{launchError}</Alert>}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer: back/cancel on the left, the next step (or Launch) on the right. */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] dark:border-gray-700 sm:px-5 sm:pb-3">
          {unavailable ? (
            <button type="button" onClick={onClose} className={`${btnPrimary} ml-auto`}>Close</button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => (step === 1 ? onClose() : goTo((step - 1) as WizardStep))}
                disabled={launching}
                className={btnGhost}
              >
                {step === 1 ? 'Cancel' : 'Back'}
              </button>
              <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
                {step < 4 && currentProblems.length > 0 && (
                  <p className="min-w-0 truncate text-xs text-gray-500 dark:text-gray-400" title={currentProblems[0]}>{currentProblems[0]}</p>
                )}
                {step < 4 ? (
                  <button type="button" onClick={() => goTo((step + 1) as WizardStep)} disabled={currentProblems.length > 0} className={`${btnPrimary} disabled:cursor-not-allowed`}>
                    Next
                  </button>
                ) : (
                  <button type="button" onClick={launch} disabled={launching || currentProblems.length > 0} className={`${btnPrimary} disabled:cursor-not-allowed`}>
                    {launching ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Rocket className="h-4 w-4" aria-hidden="true" />}
                    {launching ? 'Launching…' : 'Launch outreach list'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {pickerOpen && templates && (
        <TemplatePickerModal
          templates={templates}
          channel="sms"
          onPick={(t) => { setBody(t.body); setTemplate({ id: t.id, body: t.body }); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------------------------

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const notTextedTotal = (n: Partial<Record<string, number>>) => Object.values(n).reduce<number>((a, b) => a + (b ?? 0), 0);

function previewName(p: OutreachPreflightResponse): string {
  const c = p.preview_contact;
  return [c?.first_name, c?.last_name].filter(Boolean).join(' ') || 'this person';
}

/** Public title first (what patients see), the internal title muted, and the study's status. */
function StudyLabel({ study }: { study: StudyOption }) {
  const status = studyStatusMeta(study.status);
  return (
    <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <span className="min-w-0 truncate text-sm font-medium text-gray-900 dark:text-gray-100">{study.title || study.slug}</span>
      {study.internal_title && study.internal_title !== study.title && (
        <span className="min-w-0 truncate text-xs text-gray-500 dark:text-gray-400">{study.internal_title}</span>
      )}
      <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${status.badge}`}>{status.label}</span>
    </span>
  );
}

/** The warn-only readiness line: script, prescreen, agent. */
function Readiness({ preflight }: { preflight: OutreachPreflightResponse }) {
  const r = preflight.readiness;
  const items: Array<{ ok: boolean; label: string }> = [
    { ok: r.script_published, label: r.script_published ? 'Recruiter script published' : 'No recruiter script yet' },
    { ok: r.prescreen_published, label: r.prescreen_published ? 'Prescreen published' : 'No prescreen yet' },
    { ok: r.agent.available, label: r.agent.available ? 'Texting agent ready' : 'Texting agent not available' },
  ];
  const anyMissing = items.some((i) => !i.ok);
  return (
    <div>
      <ul className="flex flex-wrap gap-x-3 gap-y-1 text-xs" aria-label="Study readiness">
        {items.map((i) => (
          <li key={i.label} className={`inline-flex items-center gap-1 ${i.ok ? 'text-green-700 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
            {i.ok ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : <Minus className="h-3.5 w-3.5" aria-hidden="true" />}
            {i.label}
          </li>
        ))}
      </ul>
      {anyMissing && (
        <p className={`mt-1 ${mutedCls}`}>None of these stop the list — recruiters just work faster with a script and a prescreen.</p>
      )}
    </div>
  );
}

function Preview({ heading, note, text }: { heading: string; note: string; text: string | null }) {
  return (
    <div className={cardCls}>
      <p className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">
        <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" /> {heading}
      </p>
      {text ? (
        <>
          <p className="mt-2 max-w-[28rem] whitespace-pre-wrap break-words rounded-2xl rounded-bl-sm bg-gray-100 px-3 py-2 text-sm text-gray-900 dark:bg-gray-700 dark:text-gray-100">
            {text}
          </p>
          <p className={`mt-1 ${mutedCls}`}>{note}</p>
        </>
      ) : (
        <p className={`mt-1 ${mutedCls}`}>Nobody on the list to preview with yet.</p>
      )}
    </div>
  );
}

function NotTexted(p: {
  notTexted: Partial<Record<string, number>>;
  rawNotInterested: number;
  rawTooOld: number;
  includeNotInterested: boolean;
  onIncludeNotInterested: (v: boolean) => void;
  oldOverride: boolean;
  onOldOverride: (v: boolean) => void;
  oldLeadWarn: number;
  warnDays: number;
}) {
  const rows = notTextedRows(p.notTexted);
  if (rows.length === 0 && p.rawNotInterested === 0 && p.rawTooOld === 0 && p.oldLeadWarn === 0) return null;
  return (
    <div className={cardCls}>
      {rows.length > 0 ? (
        <>
          <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Not texted — they stay on the list for calls</p>
          <ul className="mt-1 space-y-0.5 text-sm">
            {rows.map((r) => (
              <li key={r.code} className="flex items-baseline gap-2 text-gray-800 dark:text-gray-100">
                <span className="min-w-0 flex-1">{r.label}</span>
                <span className="tabular-nums font-medium text-gray-700 dark:text-gray-200">{r.count.toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Everyone on the list can be texted.</p>
      )}
      {(p.rawNotInterested > 0 || p.rawTooOld > 0) && (
        <div className="mt-2 space-y-1.5 border-t border-gray-100 pt-2 dark:border-gray-700">
          {p.rawNotInterested > 0 && (
            <label className="flex items-start gap-2 text-sm text-gray-800 dark:text-gray-100">
              <input type="checkbox" checked={p.includeNotInterested} onChange={(e) => p.onIncludeNotInterested(e.target.checked)} className={checkCls} />
              <span>Also text the {p.rawNotInterested.toLocaleString()} who said they weren't interested last time</span>
            </label>
          )}
          {p.rawTooOld > 0 && (
            <label className="flex items-start gap-2 text-sm text-gray-800 dark:text-gray-100">
              <input type="checkbox" checked={p.oldOverride} onChange={(e) => p.onOldOverride(e.target.checked)} className={checkCls} />
              <span>
                Also text the {p.rawTooOld.toLocaleString()} who last heard from us over a year ago
                <span className="block text-xs text-gray-500 dark:text-gray-400">Only if you're sure we may still text them — this choice is recorded.</span>
              </span>
            </label>
          )}
        </div>
      )}
      {p.oldLeadWarn > 0 && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
          {peoplePhrase(p.oldLeadWarn)} last heard from us more than {p.warnDays.toLocaleString()} days ago — expect fewer replies from them.
        </p>
      )}
    </div>
  );
}

function StartAndPace(p: {
  startChoice: StartChoice;
  onStartChoice: (c: StartChoice) => void;
  pickedStart: string;
  onPickedStart: (v: string) => void;
  now: Date;
  paceText: string;
  onPace: (v: string) => void;
  paceHint: string;
  what: string;
  outcome: string;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div>
        <p className={labelCls}>Start</p>
        <div className="mt-1">
          <SegmentedControl options={START_OPTIONS} value={p.startChoice} onChange={(v) => p.onStartChoice(v as StartChoice)} label="When to start" />
        </div>
        {p.startChoice === 'pick' && (
          <input
            type="datetime-local"
            value={p.pickedStart}
            min={localInputValue(p.now)}
            max={localInputValue(new Date(p.now.getTime() + MAX_START_DAYS * 86400000))}
            onChange={(e) => p.onPickedStart(e.target.value)}
            aria-label="Start time"
            className={inputCls}
          />
        )}
      </div>
      <div>
        <label htmlFor="outreach-pace" className={labelCls}>Pace</label>
        <div className="mt-1 flex items-center gap-2">
          <input
            id="outreach-pace"
            type="number"
            inputMode="numeric"
            min={PACE_MIN}
            max={PACE_MAX}
            value={p.paceText}
            onChange={(e) => p.onPace(e.target.value)}
            className={`w-24 ${fieldCls}`}
          />
          <span className="text-sm text-gray-600 dark:text-gray-300">{p.what} an hour</span>
        </div>
        <p className={`mt-1 ${mutedCls}`}>{p.paceHint}</p>
      </div>
      <p className="text-sm text-gray-700 dark:text-gray-200 sm:col-span-2">
        {capitalize(p.outcome)}. Texts only go out between 8am and 9pm.
      </p>
    </div>
  );
}

function ReviewRow({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="grid gap-0.5 px-3 py-2 sm:grid-cols-[8rem_minmax(0,1fr)] sm:gap-3">
      <dt className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 sm:pt-0.5">{term}</dt>
      <dd className="text-gray-800 dark:text-gray-100">{children}</dd>
    </div>
  );
}

